import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  getParameterByName,
  hasValue,
  isAutoFilled,
  scrollToCenter,
  ProjectTypes,
  toggleCssClass,
  getProjectType,
  NOT_EMPTY_COMPARE,
} from '@r-and-a-shared-ui/utils';
import stylesSSO from './scss/FormTextInputSSO.module.scss';
import stylesPP from './scss/FormTextInputPP.module.scss';
import { BaseFormFieldProps } from '../FormField/FormField';
import { useFormContext } from 'react-hook-form';
import {
  FormFieldIconParams,
  formFieldIcons,
} from '../config/form-field-icons';
import { FormFieldTypes } from '../config/form-field-config';
import { getErrorMessageForCurrentField } from '../../helpers/form-helpers';

export type FormTextInputProps = BaseFormFieldProps;

export const FormTextInput = (props: FormTextInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const projectType = getProjectType();

  const {
    name,
    fieldRef,
    placeholder,
    label,
    isDisabled,
    fieldType,
    queryParamKey,
    isHidden,
    hideIcon = false,
    dependsOn = '',
    dependencyCriteria,
    hint,
    disableEnterSubmit = false,
  } = props;

  const {
    register,
    formState: { errors },
    setValue,
    getFieldState,
    watch,
    trigger,
  } = useFormContext();
  const value = watch(name);
  const dependsOnValue = watch(dependsOn);

  const styles =
    projectType === ProjectTypes.PlayerPortals ? stylesPP : stylesSSO;

  useEffect(() => {
    if (hasValue(queryParamKey)) {
      setValue(name, getParameterByName(queryParamKey));
    }
  }, [name, queryParamKey, setValue]);

  const checkAutoFill = useCallback(() => {
    const isFilled = isAutoFilled(inputRef?.current);
    toggleCssClass(
      inputRef?.current as HTMLElement | null,
      styles['-hasvalue'],
      isFilled || hasValue(value),
    );
  }, [inputRef, value]);

  useLayoutEffect(() => {
    // Hacked Chrome and Edge bugs on autofill
    const checkAutoFillTimeout = setTimeout(checkAutoFill, 1000);

    return () => {
      clearTimeout(checkAutoFillTimeout);
    };
  }, [checkAutoFill]);

  useEffect(() => {
    if (!hasValue(dependsOn) || dependencyCriteria !== NOT_EMPTY_COMPARE) {
      return;
    }
    trigger(name);
  }, [dependsOnValue]);

  const { ref, ...rest } = register(name);

  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const formFieldIconParams: FormFieldIconParams = {
    fieldHasError: hasValue(errors?.[name]),
    value,
    showPassword,
    toggleShowPassword,
    className: styles['form-element__icon'],
    hideIcon,
    projectType: projectType as ProjectTypes,
  };

  if (isHidden) {
    return null;
  }

  return (
    <div
      ref={fieldRef}
      className={`${styles['form-element']} ${styles['form-input']} ${
        errors?.[name] ? styles['form-element__has-error'] : ''
      }`}
      onFocus={() => scrollToCenter(fieldRef)}
    >
      <input
        id={name}
        className={`${styles['form-element__input']}`}
        type={fieldType === 'password' && showPassword ? 'text' : fieldType}
        autoComplete={value}
        defaultValue={value}
        placeholder={placeholder}
        disabled={isDisabled}
        style={{ opacity: !hasValue(label) ? 1 : undefined }}
        onKeyDown={
          disableEnterSubmit
            ? e => e.key === 'Enter' && e.preventDefault()
            : undefined
        }
        {...rest}
        ref={e => {
          ref(e);
          inputRef.current = e;
        }}
      />
      {formFieldIcons[fieldType as FormFieldTypes]?.(formFieldIconParams)}
      <div className={styles['form-element__bar']}></div>
      {hasValue(label) && (
        <label className={styles['form-element__label']} htmlFor={name}>
          {label}
        </label>
      )}
      {errors?.[name] ? (
        <small className={styles['form-element__hint']}>
          {getErrorMessageForCurrentField(getFieldState, props)}
        </small>
      ) : (
        hint ? (
          <small className={styles['form-element__hint']}>
            {hint}
          </small>
        ) : null
      )}
    </div>
  );
};

export default FormTextInput;
